import {
  ArrowRight,
  ShoppingBag
} from "lucide-react";
import { Link } from "react-router";

import type { Product } from "../../types/catalog";
import { useCart } from "../../context/CartContext";
import {
  primaryProductImage,
  productImageUrl
} from "../../utils/product-image";

interface ProductCardProps {
  product: Product;
}

const tones = [
  "from-[#d4b18a] to-[#76553e]",
  "from-[#a9b99b] to-[#4f6245]",
  "from-[#a16573] to-[#583641]",
  "from-[#d8a29b] to-[#945d58]"
];

export function ProductCard({

  product
}: ProductCardProps) {
  const { addItem } = useCart();
  const tone =
    tones[
      product.name.length % tones.length
    ];

  const primaryImage =
    primaryProductImage(
      product.images
    );

  const isOutOfStock =
    product.stockQuantity <= 0;

  const isLowStock =
    product.stockQuantity > 0 &&
    product.stockQuantity <= 5;

  return (
    <article className="group overflow-hidden rounded-[2rem] border border-[#5e3b2c]/10 bg-white transition duration-300 hover:-translate-y-1 hover:shadow-xl">
      <Link
        to={`/products/${product.slug}`}
        className={[
          "relative flex aspect-square items-center justify-center overflow-hidden",
          primaryImage
            ? "bg-[#eee5da]"
            : `bg-gradient-to-br ${tone}`
        ].join(" ")}
      >
        {primaryImage ? (
          <img
            src={productImageUrl(
              primaryImage.id
            )}
            alt={
              primaryImage.altText ??
              product.name
            }
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <span className="font-serif text-4xl text-white/90">
            RiNi
          </span>
        )}

        {product.isBestSeller && (
          <span className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#5e3b2c] shadow-sm">
            Bestseller
          </span>
        )}
      </Link>

      <div className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#70845b]">
          {product.category.name}
        </p>

        <Link
          to={`/products/${product.slug}`}
          className="mt-2 block min-h-14 font-serif text-xl text-[#38251e] transition group-hover:text-[#70845b]"
        >
          {product.name}
        </Link>

        <p className="mt-2 line-clamp-2 min-h-12 text-sm leading-6 text-[#746d65]">
          {product.shortDescription}
        </p>

        <div className="mt-4">
          {isOutOfStock ? (
            <span className="inline-flex rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
              Out of stock
            </span>
          ) : isLowStock ? (
            <span className="inline-flex rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
              Only {product.stockQuantity} left
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-[#edf1e8] px-3 py-1.5 text-xs font-semibold text-[#526746]">
              In stock
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-lg font-semibold text-[#5e3b2c]">
              ₹{Number(product.price).toLocaleString("en-IN")}
            </span>

            {product.compareAtPrice && (
              <span className="ml-2 text-sm text-[#746d65] line-through">
                ₹{Number(product.compareAtPrice).toLocaleString("en-IN")}
              </span>
            )}
          </div>

          <button
            type="button"
            disabled={isOutOfStock}
            className={[
              "flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-white transition sm:h-10 sm:min-h-0 sm:w-10 sm:px-0",
              isOutOfStock
                ? "cursor-not-allowed bg-[#b8afa9]"
                : "bg-[#5e3b2c] hover:bg-[#493027]"
            ].join(" ")}
            aria-label={
              isOutOfStock
                ? `${product.name} is out of stock`
                : `Add ${product.name} to cart`
            }
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (!isOutOfStock) {
                addItem(product);
              }
            }}
          >
            <ShoppingBag size={17} />

            <span className="sm:hidden">
              {isOutOfStock
                ? "Out of stock"
                : "Add to cart"}
            </span>
          </button>
        </div>

        <Link
          to={`/products/${product.slug}`}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#5e3b2c]"
        >
          View product
          <ArrowRight size={15} />
        </Link>
      </div>
    </article>
  );
}
